import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from "react";
import { CustomersViewModel } from "../../viewmodels/customers/CustomersViewModel";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import * as actions from "../../store/actions/index";
import { classNames, withRouter } from "../../shared/utility";

// Template Components
import DataManagementTemplate from "../../components/templates/DataManagementTemplate";
import MobileCardsTemplate from "../../components/templates/MobileCardsTemplate";

// Other Components
import SlideOver from "../../components/slideOver/SlideOver";
import EmptyModal from "../../components/modals/EmptyModal";
import CustomersBulkActions from "../../components/forms/customer/modals/CustomersBulkActions";
import type { DataColumn } from "../../components/templates/DataManagementTemplate";
import type { PaginatorPageChangeEvent } from "primereact/paginator";
import type { AppDispatch, RootState } from "../../index";
import type { CustomerBulkActionValues, CustomerListItem, CustomerSalesRep } from "../../types/customers";

interface CustomersProps { logout: () => void }

function Customers(props: CustomersProps) {
  const { logout } = props;
  const navigate = useNavigate();
  const {
    emptyModal,
    setEmptyModelOpen,
    getCustomers,
    loading,
    customers,
    setSlideOver,
    slideOver,
    getCustomerGroupsForDropdown,
    customerGroupOptions,
    onBulkAction,
    askDeleteCustomer,
  } = CustomersViewModel(logout);

  var currentPage = 1;
  useEffect(() => {
    getCustomerGroupsForDropdown();
    getCustomers(null, currentPage);
  }, []);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");

  // Selection state for bulk actions
  const checkbox = useRef<HTMLInputElement>(null);
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerListItem[]>([]);

  const customersList = customers?.data ?? [];
  const userType = localStorage.getItem("userType");

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedCustomers.length > 0 && selectedCustomers.length < customersList.length;
    setChecked(selectedCustomers.length === customersList.length && customersList.length > 0);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
  }, [selectedCustomers, customersList.length]);

  function toggleAll() {
    setSelectedCustomers(checked || indeterminate ? [] : customersList);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  // Search handler
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    getCustomers(term || null, currentPage);
  };

  // Pagination handler
  const handlePageChange = (event: PaginatorPageChangeEvent) => {
    currentPage = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    getCustomers(searchTerm || null, currentPage);

    // Scroll to top when paginating
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Row click handler
  const handleRowClick = (customer: CustomerListItem) => {
    navigate(`/customers/${customer.id}`);
  };

  const formatSalesReps = (reps?: CustomerSalesRep[]) => {
    if (!reps || !Array.isArray(reps) || reps.length === 0) return "";
    if (reps.length === 1) {
      const r = reps[0];
      return [r.first_name, r.last_name].filter(Boolean).join(" ").trim() || "";
    }
    if (reps.length === 2) {
      return (
        reps
          .map((r) => [r.first_name, r.last_name].filter(Boolean).join(" ").trim())
          .filter(Boolean)
          .join(", ") || ""
      );
    }
    return `${reps.length}+`;
  };

  // Button configurations
  const headerButtons = [
    {
      label: "Bulk Actions",
      onClick: () =>
        setEmptyModelOpen({
          childComponent: (
            <CustomersBulkActions
              customerGroupOptions={customerGroupOptions}
              confirmAction={(bulkActionValues: CustomerBulkActionValues) =>
                onBulkAction({
                  selectedCustomers: [...selectedCustomers],
                  ...bulkActionValues,
                })
              }
              setOpen={setEmptyModelOpen}
            />
          ),
          open: true,
        }),
      className:
        "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white focus:ring-primary-500",
    },
  ];

  // Column configurations for DataTable (without checkbox - added in template)
  const columns: DataColumn<CustomerListItem>[] = [
    {
      field: "company_name",
      header: "Name",
      style: { width: "25%" },
      body: (rowData) => (
        <span
          className={classNames(
            "font-semibold apollo-subheading",
            selectedCustomers.includes(rowData) ? "text-primary-600" : "text-gray-900"
          )}
        >
          {rowData.company_name}
        </span>
      ),
    },
    {
      field: "contact",
      header: "Contact",
      style: { width: "20%" },
      body: (rowData) => (
        <span className="apollo-body">
          {rowData.given_name} {rowData.family_name}
        </span>
      ),
    },
    {
      header: "Customer Group",
      style: { width: "20%" },
      body: (rowData) => <span className="apollo-body">{rowData.group_name}</span>,
    },
    ...(Number(userType) === 1
      ? [
          {
            header: "Sales persons",
            style: { width: "20%" },
            body: (rowData: CustomerListItem) => (
              <span className="apollo-body">{formatSalesReps(rowData.sales_reps)}</span>
            ),
          },
        ]
      : []),
    {
      header: "Actions",
      style: { width: "10%" },
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <Link
            className="inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            to={`/customers/${rowData.id}`}
          >
            View
          </Link>
          <button
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              askDeleteCustomer(rowData);
            }}
            className="inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // Mobile cards component
  const CustomersMobileCards = ({ data }: { data: CustomerListItem[] }) => (
    <MobileCardsTemplate
      data={data}
      onCardClick={(customer) => navigate(`/customers/${customer.id}`)}
      onEdit={(customer) => navigate(`/customers/${customer.id}`)}
      editRoute="/customers"
      emptyMessage="No customers found"
      cardContent={(customer) => (
        <div className="flex flex-col space-y-3">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-h6 apollo-heading text-primary-600 mb-1">
                {customer.company_name}
              </h3>
              <p className="text-body-2 apollo-subheading">
                {customer.given_name} {customer.family_name}
              </p>
            </div>
            {customer.group_name && (
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-base font-medium bg-blue-100 text-blue-800">
                {customer.group_name}
              </span>
            )}
          </div>

          {/* Details */}
          <div className="space-y-2">
            {(customer.sales_reps?.length ?? 0) > 0 && (
              <div className="flex justify-between text-body-2 apollo-body">
                <span>Allocated to:</span>
                <span>
                  {Array.isArray(customer.sales_reps) ? formatSalesReps(customer.sales_reps) : null}
                </span>
              </div>
            )}
            {customer.email && (
              <div className="flex justify-between text-body-2 apollo-body">
                <span>Email:</span>
                <span>{customer.email}</span>
              </div>
            )}
          </div>
        </div>
      )}
      actionButtons={(customer) => (
        <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
          <Link to={`/customers/${customer.id}`} className="flex-1">
            <button className="w-full inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
              View
            </button>
          </Link>
          <button
            type="button"
            onClick={() => askDeleteCustomer(customer)}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
        </div>
      )}
    />
  );

  return (
    <div>
      <DataManagementTemplate
        title="Customers"
        description="Manage your customer database"
        headerButtons={headerButtons}
        columns={[
          {
            header: (
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                ref={checkbox}
                checked={checked}
                onChange={toggleAll}
              />
            ),
            style: { width: "5%" },
            body: (rowData) => (
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={selectedCustomers.includes(rowData)}
                onChange={(e) =>
                  setSelectedCustomers(
                    e.target.checked
                      ? [...selectedCustomers, rowData]
                      : selectedCustomers.filter((c) => c !== rowData)
                  )
                }
              />
            ),
          },
          ...columns,
        ]}
        data={customersList}
        loading={loading}
        pagination={{
          first: first,
          rows: rows,
          total: customers?.total ?? 0,
          template:
            "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
          currentPageReportTemplate: "Showing {first} to {last} of {totalRecords} customers",
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        searchPlaceholder="Search by customer name"
        MobileCardsComponent={CustomersMobileCards}
        emptyMessage="No customers found"
      />
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (_state: RootState) => {
  return {};
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Customers));
